import { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  RadialBarChart, RadialBar, Legend,
} from "recharts";
import {
  Activity, MessageSquare, Star, IndianRupee, TrendingUp, Mail,
  AlertTriangle, CheckCircle2, Clock, Zap,
} from "lucide-react";

const RED = "#E50914";
const ACCENT = "#22D3EE"; // cyan accent for delightful contrast
const GREEN = "#10B981";
const YELLOW = "#F59E0B";

const fmtInr = (n) => `Rs ${Number(n || 0).toLocaleString("en-IN")}`;

function chipColorForLevel(level) {
  switch (level) {
    case "exhausted": return "bg-red-900/40 text-red-300 border-red-700";
    case "critical": return "bg-red-600/15 text-red-400 border-red-600/60";
    case "warning": return "bg-yellow-500/15 text-yellow-400 border-yellow-500/60";
    default: return "bg-green-500/10 text-green-400 border-green-500/40";
  }
}

function KpiCard({ label, value, sub, icon: Icon, accent = "white", testid }) {
  const color = {
    white: "text-white", red: "text-red-500", green: "text-green-400",
    yellow: "text-yellow-400", cyan: "text-cyan-400",
  }[accent] || "text-white";
  const bar = {
    white: "from-white/40 to-white/0", red: "from-red-600 to-red-600/0",
    green: "from-green-500 to-green-500/0", yellow: "from-yellow-500 to-yellow-500/0",
    cyan: "from-cyan-400 to-cyan-400/0",
  }[accent] || "from-white/40 to-white/0";
  return (
    <div data-testid={testid} className="relative border border-white/10 bg-gradient-to-b from-[#141414] to-[#0E0E0E] p-5 overflow-hidden group hover:border-white/30 transition-colors">
      <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${bar}`} />
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">{label}</p>
          <p className={`text-3xl sm:text-4xl font-black ${color} mt-2 tracking-tight`}>{value}</p>
          {sub && <p className="text-[11px] text-zinc-500 mt-1 truncate">{sub}</p>}
        </div>
        {Icon && (
          <div className="shrink-0 w-10 h-10 border border-white/10 bg-black/40 flex items-center justify-center group-hover:border-white/30 transition-colors">
            <Icon size={16} className={color} />
          </div>
        )}
      </div>
    </div>
  );
}

function ChartCard({ title, sub, children, right }) {
  return (
    <div className="border border-white/10 bg-[#0E0E0E] p-5">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-white">{title}</h3>
          {sub && <p className="text-[11px] text-zinc-500 mt-1">{sub}</p>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function TickStyle() { return { fontSize: 10, fill: "#666", fontFamily: "inherit" }; }

export default function OverviewPanel({ API, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/analytics/overview?days=${d}`, auth);
      setData(data);
    } catch (e) { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

  if (loading && !data) {
    return <div className="py-24 text-center text-xs uppercase tracking-widest text-zinc-500">Loading analytics…</div>;
  }
  if (!data) return null;

  const { totals, series, rating_distribution, email_quota, activity, permissions = {} } = data;
  const canSeeRevenue = permissions.analytics_revenue !== false;

  // Radial gauge data for quota
  const quotaRemaining = email_quota?.remaining_today ?? 100;
  const quotaLimit = email_quota?.daily_limit ?? 100;
  const quotaUsedPct = Math.max(0, Math.min(100, ((quotaLimit - quotaRemaining) / quotaLimit) * 100));
  const quotaLevel = email_quota?.level || "ok";
  const quotaFill = quotaLevel === "exhausted" ? "#7F1D1D"
    : quotaLevel === "critical" ? "#EF4444"
    : quotaLevel === "warning" ? YELLOW
    : GREEN;

  const ratingData = rating_distribution.map((r) => ({
    name: `${r.rating}★`,
    value: r.count,
    rating: r.rating,
  }));
  const totalRatingCount = ratingData.reduce((s, r) => s + r.value, 0);
  const ratingColors = ["#DC2626", "#F59E0B", "#FBBF24", "#84CC16", "#10B981"];

  return (
    <div className="space-y-6" data-testid="overview-panel">
      {/* Top KPIs */}
      <div className={`grid grid-cols-2 ${canSeeRevenue ? "sm:grid-cols-3 lg:grid-cols-5" : "sm:grid-cols-2 lg:grid-cols-4"} gap-3`}>
        {canSeeRevenue && (
          <KpiCard label="Revenue" value={fmtInr(totals.revenue_total)} sub={`${totals.paid_payments} paid · ${totals.payments} total`} icon={IndianRupee} accent="green" testid="kpi-revenue" />
        )}
        <KpiCard label="Enquiries" value={totals.contacts} sub={`${totals.unread_contacts} unread`} icon={MessageSquare} accent="cyan" testid="kpi-contacts" />
        <KpiCard label="Reviews" value={totals.reviews} sub={`${totals.pending_reviews} pending · ${totals.approved_reviews} approved`} icon={Star} accent="yellow" testid="kpi-reviews" />
        <KpiCard label="Email Today" value={email_quota ? `${email_quota.sent_today}/${email_quota.daily_limit}` : "–"} sub={email_quota ? `${email_quota.remaining_today} left` : ""} icon={Mail} accent={email_quota?.level === "ok" ? "green" : email_quota?.level === "warning" ? "yellow" : "red"} testid="kpi-email" />
        <KpiCard label="Avg Rating" value={totalRatingCount ? (ratingData.reduce((s, r) => s + r.rating * r.value, 0) / totalRatingCount).toFixed(1) : "–"} sub={`across ${totalRatingCount} reviews`} icon={TrendingUp} accent="white" testid="kpi-avg-rating" />
      </div>

      {/* Time range chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Range:</span>
        {[7, 14, 30, 60].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            data-testid={`overview-range-${d}`}
            className={`text-[11px] font-bold uppercase tracking-widest px-3 py-1 border transition-colors ${days === d ? "border-red-600 bg-red-600/15 text-red-400" : "border-white/10 text-zinc-500 hover:text-white hover:border-white/30"}`}
          >
            {d}d
          </button>
        ))}
      </div>

      {/* Charts row 1 */}
      <div className={`grid ${canSeeRevenue ? "lg:grid-cols-3" : "lg:grid-cols-1"} gap-4`}>
        {canSeeRevenue && (
        <div className="lg:col-span-2">
          <ChartCard title={`Revenue · last ${days} days`} sub="Paid invoices only">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={series.revenue} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={RED} stopOpacity={0.95} />
                    <stop offset="100%" stopColor={RED} stopOpacity={0.25} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1a1a1a" vertical={false} />
                <XAxis dataKey="date" tick={TickStyle()} axisLine={{ stroke: "#222" }} tickLine={false} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={TickStyle()} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}k` : v} />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.04)" }}
                  contentStyle={{ background: "#0A0A0A", border: "1px solid #333", borderRadius: 0, fontSize: 12, color: "#fff" }}
                  formatter={(v) => [fmtInr(v), "Revenue"]}
                />
                <Bar dataKey="value" fill="url(#revGrad)" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        )}

        <ChartCard title="Email Quota" sub="Today's usage">
          <div className="relative">
            <ResponsiveContainer width="100%" height={220}>
              <RadialBarChart
                cx="50%" cy="50%" innerRadius="65%" outerRadius="100%"
                startAngle={90} endAngle={-270}
                data={[{ name: "used", value: quotaUsedPct, fill: quotaFill }]}
              >
                <RadialBar background={{ fill: "#1a1a1a" }} dataKey="value" cornerRadius={6} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <p className="text-3xl font-black text-white">{quotaRemaining}</p>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">left today</p>
              <span className={`mt-2 text-[9px] font-black uppercase tracking-widest border px-2 py-0.5 ${chipColorForLevel(quotaLevel)}`}>{quotaLevel}</span>
            </div>
          </div>
        </ChartCard>
      </div>

      {/* Charts row 2 */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <ChartCard title="Enquiries · daily" sub={`Total received over last ${days} days`}>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={series.contacts} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="#1a1a1a" vertical={false} />
                <XAxis dataKey="date" tick={TickStyle()} axisLine={{ stroke: "#222" }} tickLine={false} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={TickStyle()} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  cursor={{ stroke: ACCENT, strokeWidth: 1, strokeDasharray: "4 4" }}
                  contentStyle={{ background: "#0A0A0A", border: "1px solid #333", borderRadius: 0, fontSize: 12, color: "#fff" }}
                />
                <Line type="monotone" dataKey="value" stroke={ACCENT} strokeWidth={2} dot={{ fill: ACCENT, r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <ChartCard title="Rating Distribution" sub={totalRatingCount ? `${totalRatingCount} reviews total` : "No reviews yet"}>
          {totalRatingCount ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={ratingData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%" cy="50%"
                  innerRadius={50} outerRadius={85}
                  paddingAngle={2}
                  stroke="none"
                >
                  {ratingData.map((_, i) => (
                    <Cell key={i} fill={ratingColors[i]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #333", borderRadius: 0, fontSize: 12, color: "#fff" }} />
                <Legend iconType="square" iconSize={8} wrapperStyle={{ fontSize: 10, color: "#666" }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[220px] text-zinc-600 text-xs uppercase tracking-widest">No data yet</div>
          )}
        </ChartCard>
      </div>

      {/* Activity Feed */}
      <ChartCard title="Recent Activity" sub="Latest enquiries, reviews and payments">
        {activity.length === 0 ? (
          <p className="text-zinc-600 text-xs uppercase tracking-widest text-center py-6">No activity yet</p>
        ) : (
          <ul className="divide-y divide-white/5">
            {activity.map((a, i) => {
              const iconByType = { contact: MessageSquare, review: Star, payment: IndianRupee };
              const colorByType = { contact: "text-cyan-400", review: "text-yellow-400", payment: "text-green-400" };
              const Icon = iconByType[a.type] || Activity;
              return (
                <li key={i} className="flex items-start gap-3 py-3 group hover:bg-white/2 px-1 -mx-1 transition-colors">
                  <div className={`w-8 h-8 shrink-0 border border-white/10 bg-black/40 flex items-center justify-center ${colorByType[a.type]}`}>
                    <Icon size={13} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white font-bold truncate">{a.title}</p>
                    {a.subtitle && <p className="text-xs text-zinc-500 truncate mt-0.5">{a.subtitle}</p>}
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-zinc-600 whitespace-nowrap">{new Date(a.at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                </li>
              );
            })}
          </ul>
        )}
      </ChartCard>
    </div>
  );
}
